import React, { useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';

const FIELDS = [
  { id: 'test_secret_key', label: 'Test Secret Key', prefix: 'sk_test_', env: 'test' },
  { id: 'test_webhook_secret', label: 'Test Webhook Signing Secret', prefix: 'whsec_', env: 'test' },
  { id: 'live_secret_key', label: 'Live Secret Key', prefix: 'sk_live_', env: 'live' },
  { id: 'live_webhook_secret', label: 'Live Webhook Signing Secret', prefix: 'whsec_', env: 'live' },
];
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.id, '']));

const fieldError = (f, v) => {
  if (!v) return null;
  if (f.prefix.startsWith('sk_') && v.startsWith(f.env === 'test' ? 'sk_live_' : 'sk_test_')) return `This is a ${f.env === 'test' ? 'live' : 'test'} key. It belongs in the ${f.env === 'test' ? 'Live' : 'Test'} field.`;
  return v.startsWith(f.prefix) ? null : `Must begin with ${f.prefix}`;
};

export const CredentialsDialog = ({ data, onClose, send }) => {
  const [vals, setVals] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [serverErr, setServerErr] = useState({});
  const [editable, setEditable] = useState({});
  const errors = FIELDS.map((f) => fieldError(f, vals[f.id].trim()) || serverErr[f.id]);
  const any = FIELDS.some((f) => vals[f.id].trim());
  const close = () => { setVals(EMPTY); setServerErr({}); onClose(); };
  const change = (id, v) => { setVals({ ...vals, [id]: v }); setServerErr({ ...serverErr, [id]: null }); };
  const save = async () => {
    setSaving(true);
    const body = Object.fromEntries(FIELDS.filter((f) => vals[f.id].trim()).map((f) => [f.id, vals[f.id].trim()]));
    const ok = await send('put', '/providers/stripe/credentials', body, 'Stripe credentials saved. The site is still in TEST MODE.', (d) => setServerErr(d?.fields || {}));
    setSaving(false);
    if (ok) close();
  };
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="stripe-credentials-dialog">
        <DialogHeader>
          <DialogTitle>Edit Stripe Credentials</DialogTitle>
          <DialogDescription>Leave a field empty to keep what is saved. Values are encrypted on the server and never shown again. Saving never switches the site to Live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {['test', 'live'].map((env) => (
            <fieldset key={env} className="border rounded-md p-3 space-y-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-neutral-500">{env}</legend>
              {FIELDS.filter((f) => f.env === env).map((f) => {
                const i = FIELDS.indexOf(f);
                const saved = data[env][f.id.endsWith('secret_key') ? 'secret_key' : 'webhook_secret'].hint;
                return (
                  <label key={f.id} className="block text-sm">
                    <span className="font-medium">{f.label}</span>
                    <Input type="password" name={`apollo-${f.id}`} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" spellCheck={false}
                      readOnly={!editable[f.id]} onFocus={() => setEditable({ ...editable, [f.id]: true })} className="mt-1 font-mono" placeholder={saved ? `Saved ${saved} · enter a new value to replace` : `${f.prefix}…`}
                      value={vals[f.id]} onChange={(e) => change(f.id, e.target.value)} data-testid={`cred-input-${f.id}`} />
                    {errors[i] && <span className="text-xs text-red-600" data-testid={`cred-error-${f.id}`}>{errors[i]}</span>}
                  </label>
                );
              })}
            </fieldset>
          ))}
          {!any && <p className="text-xs text-neutral-500" data-testid="cred-save-hint">Enter a new value only in the field you want to replace. Empty fields keep their saved value.</p>}
          {errors.some(Boolean) && <p className="text-xs text-red-600" data-testid="cred-save-blocked">Save is blocked until the field errors above are fixed. Nothing has been changed.</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={close} data-testid="cred-cancel">Cancel</Button>
            <Button disabled={!any || errors.some(Boolean) || saving} onClick={save} data-testid="cred-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}Save / Update
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
